<!-- eslint-disable vue/multi-word-component-names -->
<script lang="ts" setup>
import { useToggle } from '@vueuse/core'
import { ChevronDownIcon } from 'mdi-vue3'
import { computed, type DefineComponent, type HTMLAttributes } from 'vue'
import { useI18n } from 'vue-i18n'

import { DefinesVisibility, isVisible } from '../../../lib/utils'
import { List, type ListItemInterface } from '../list'

export type MenuProperties = {
  title?: string
  icon?: DefineComponent
  expandable?: boolean
  collapsed?: boolean
  level?: number
  /**
   * The label of the button
   */
  items?: ListItemInterface[]
  class?: HTMLAttributes['class']
} & DefinesVisibility

const { t } = useI18n({
  messages: {
    en: {
      menu: 'Menu',
    },
    nl: {
      menu: 'Menu',
    },
  },
})

defineOptions({ inheritAttrs: false })

const properties = withDefaults(defineProps<MenuProperties>(), {
  title: '',
  expandable: false,
  items: () => [],
  collapsed: false,
  level: 0,
  visible: true,
  icon: undefined,
  class: undefined,
})

const [isExpanded, toggleExpandable] = useToggle(true)
// eslint-disable-next-line unicorn/prefer-logical-operator-over-ternary
const titleLabel = computed(() => (properties.title ? properties.title : properties.title === undefined ? t('menu') : ''))

const isMenuVisible = computed(() => {
  return isVisible(properties)
})
</script>

<template>
  <div
    v-show="isMenuVisible"
    :class="[
      'atabix-menu',
      { 'atabix-menu--expanded': isExpanded, 'atabix-menu--collapsed': properties.collapsed, 'atabix-menu--top': properties.level === 0 },
      properties.class,
    ]"
  >
    <slot v-if="!collapsed" name="title">
      <div
        v-if="titleLabel || expandable"
        class="atabix-menu__title"
        role="button"
        tabindex="0"
        @click="toggleExpandable()"
        @keydown="toggleExpandable()"
      >
        <div class="atabix-menu__title-container">
          <component :is="icon" v-if="icon" class="atabix-menu__title-icon" />
          {{ titleLabel || t('menu') }}
        </div>
        <ChevronDownIcon v-if="expandable" class="atabix-menu__expand" />
      </div>
    </slot>

    <div v-show="isExpanded || collapsed" class="atabix-menu__content">
      <slot>
        <List :collapsed="collapsed" :items="items" :level="level" />
      </slot>
    </div>
  </div>
</template>

<style lang="scss">
.atabix-menu {
  @apply transition-all duration-300;
}

.atabix-menu--top {
  @apply w-auto;
}

.atabix-menu__content {
  //
}

.atabix-menu__expand {
  @apply size-5;
  @apply cursor-pointer select-none;

  @apply transform-gpu;
  transition: all 0.5s;
}

.atabix-menu--collapsed {
  min-width: auto;
  &.atabix-menu--top {
    @apply w-auto;
  }
}

.atabix-menu--expanded {
  .atabix-menu__expand {
    transform: rotate(-180deg) !important;
  }
}

.atabix-menu--open {
  @apply min-w-[16rem];

  .atabix-menu__expand {
    transform: rotate(-180deg) !important;
  }
}

.atabix-menu__title {
  @apply flex flex-row items-center justify-between;
  @apply text-base font-semibold;
  @apply leading-5;
  @apply p-1.5;
}

.atabix-menu__title-container {
  @apply flex flex-row items-center gap-2;
}

.atabix-menu__title-icon {
  @apply size-5;
}
</style>
